import React, { useEffect, useState, useRef } from 'react';
import Button from 'components/buttons/Button';
import classNames from 'classnames';
import { PiAndroidLogoFill } from 'react-icons/pi';
import { FaApple } from 'react-icons/fa';
import { GoShare } from "react-icons/go";
import { MdOutlineAddBox } from "react-icons/md";
import { PiDeviceMobileLight } from "react-icons/pi";
import Images from "assets/images";
import useStandalone from 'hooks/useStandalone';
import { useCurrentUser } from 'hooks/useCurrentUser';
import ApiConfig from 'api/config';

let pwaFlagUpdateStarted = false;

export default function PWABanner({ hideImage = false, className = '' }) {
  const promptRef = useRef(null);
  const [available, setAvailable] = useState(false);
  const { isStandalone } = useStandalone();
  const { userDetails, userToken } = useCurrentUser();
  
  useEffect(() => {
    if (window.__MY_APP_PWA__?.deferredPrompt) {
      promptRef.current = window.__MY_APP_PWA__.deferredPrompt;
      setAvailable(true);
    }
  
    const onAvail = () => {
      promptRef.current = window.__MY_APP_PWA__.deferredPrompt;
      setAvailable(Boolean(promptRef.current));
    };
    window.addEventListener('myapp:pwa-install-available', onAvail);
    return () => window.removeEventListener('myapp:pwa-install-available', onAvail);
  }, []);

  const onClick = async () => {
    const p = promptRef.current;
    if (!p) {
      return;
    }
    p.prompt();
    await p.userChoice;
    promptRef.current = null;
    window.__deferredPrompt = null;
    setAvailable(false);
  };

  useEffect(() => {
    if (!isStandalone || userDetails?.has_downloaded_pwa !== false || !userToken || pwaFlagUpdateStarted) return;
    pwaFlagUpdateStarted = true;
    const url = `${ApiConfig.baseUrl}${ApiConfig.urls.user.updatePWAFlag}`;
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Token': userToken,
        'Device-Platform': 'Web',
      },
      body: JSON.stringify({ has_downloaded_pwa: true }),
    }).catch((error) => console.error('Error updating PWA flag:', error));
  }, [isStandalone, userDetails?.has_downloaded_pwa, userToken]);

  if (isStandalone || userDetails?.has_downloaded_pwa) {
    return null;
  }
  return <section className={classNames('relative flex flex-col md:flex-row space-y-12 bg-[#EEFDFD] border-2 border-blue rounded-3xl overflow-auto', !hideImage && 'lg:max-w-homeScreen', className)}>
  <div className={classNames(`flex flex-col items-start justify-between ${hideImage ? 'w-full' : 'md:w-3/4'} space-y-12 px-4 md:px-10 py-4 md:py-6`)}>
    <div className='relative md:absolute md:top-6 md:right-8 bg-gradient-to-r from-[#53B3DE] via-[#53B3DE] to-[#4ED8DC] rounded-2xl p-2 flex items-center space-x-1 mb-4 md:mb-0'>
      <PiDeviceMobileLight className='w-8 h-8 text-white' /> <span className='text-white text-lg font-medium'>SkyMD Mobile App</span>
    </div>
    <div className='flex flex-col items-start !mt-0 md:!mt-2 space-y-6'>
      <h2 className="text-2xl md:text-4xl font-bold text-center md:text-left inline-block text-transparent bg-clip-text bg-gradient-to-r from-[#53B3DE] via-[#53B3DE] to-[#4ED8DC]">Your Care, One Tap Away</h2>
      <div className='flex flex-col items-start justify-start space-y-2'>
      <p className='text-lg md:text-xl font-medium text-[#9CA3AF] text-center md:text-left'>For faster services, follow the instructions to install.</p>
      <ul>
        <li className='text-lg md:text-xl font-medium text-[#9CA3AF] text-left list-disc list-inside'> No need to go to the app store; follow the instructions below.</li>
      </ul>
      </div>
    </div>
    <div className='flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 items-start justify-between w-full'>
    <div className="w-full flex space-x-4 bg-white rounded-xl p-4 border border-[#EDEDED]">
        <div className='p-3 rounded-xl bg-[#EEFDFD] w-fit h-fit mb-4'>
          <FaApple className='w-8 h-8 text-[#53D7E9]' />
        </div>
        <div className='w-full flex flex-col items-start justify-start space-y-4'>
          <div className='flex flex-col items-start justify-start space-y-1'>
            <h3 className='text-lg text-[#121549] font-medium'>On iPhone or iPad?</h3>
            <p className="text-[#121549E5] font-normal text-base">Follow these simple steps:</p>
          </div>
          <div className='flex flex-col items-start justify-start space-y-2 w-full'>
            <div className='flex items-center justify-between space-x-2 w-full bg-grey-600/15 rounded-xl p-2'>
              <span className='font-bold bg-themeBlack/20 text-themeBlack rounded-full w-7 h-7 flex items-center justify-center'>1</span> 
              <span className='text-base text-[#121549] font-medium flex-1'>Tap Share</span>
              <GoShare className='w-6 h-6 text-themeBlack' strokeWidth={0.4} />
            </div>
            <div className='flex items-center justify-between space-x-2 w-full bg-grey-600/15 rounded-xl p-2'>
              <span className='font-bold bg-themeBlack/20 text-themeBlack rounded-full w-7 h-7 flex items-center justify-center'>2</span>
              <span className='text-base text-[#121549] font-medium flex-1'>Add to Home Screen</span>
              <MdOutlineAddBox className='w-6 h-6 text-themeBlack' />
            </div>
          </div>
        </div>
      </div>
      <div className="w-full flex space-x-4 bg-white rounded-xl p-4 border border-[#EDEDED]">
        <div className='p-3 rounded-xl bg-[#EEFDFD] w-fit h-fit mb-4'>
        <PiAndroidLogoFill className='w-8 h-8 text-[#53D7E9]' />
        </div>
        <div className='w-full flex flex-col items-start justify-start space-y-2'>
          <div className='flex flex-col items-start justify-start space-y-1'>
            <h3 className='text-lg text-[#121549] font-medium'>On Android?</h3>
            <p className="text-[#121549E5] font-normal text-base">Just tap below and select Install when prompted.</p>
          </div>
          <Button label="Install App" onClick={onClick} className='w-full h-10 !p-4 text-lg max-w-[200px]' disabled={!available} />
        </div>
      </div>
    </div>
  </div>
  {!hideImage && <div className="relative hidden md:flex justify-end items-center md:w-1/4 h-full !mt-auto">
      <div className="relative overflow-hidden w-full max-w-sm lg:max-w-2xl aspect-square">
        <img src={Images.PWAImage} alt="SkyMD App on mobile device" className="absolute -bottom-[27%] -right-[3%] w-full h-full object-contain scale-[1.25]"/>
      </div>
    </div>}
  </section>;
}